import {
  EventStreamContentType,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { API_SUCCESS_CODE, SESSION_PAGE_SIZE } from "@/constants";
import {
  addFeedback,
  closeSession,
  fetchAppConfig,
  fetchSessionDetail,
  fetchSessionList,
  updateSessionTitle,
} from "@/services/api";
import type { Event } from "@/types";
import { safeJsonParse } from "@/utils";
import type { AdkChatTransport } from "@/core/runtime";

export interface XGroupOpenApiTransportOptions {
  url: string;
  appNo: string;
  token?: string | (() => string | undefined);
  pageSize?: number;
  onAuthError?: (response: Response) => void;
}

/** 支持传入 token 字符串或 token getter，便于业务侧刷新 token 后无需重建 transport。 */
const getTokenValue = (token?: string | (() => string | undefined)) =>
  typeof token === "function" ? token() : token;

/**
 * 兼容后端新旧两种 stream 包装格式。
 * 新格式通常是 { data, result }，runtime 只消费标准 ADK Event。
 */
const normalizeStreamEvent = (rawData: any): Event => {
  if (rawData?.data && rawData?.result) {
    return {
      ...rawData.data,
      content: rawData.data,
      invocationId: rawData.result.invocationId,
      sessionId: rawData.result.sessionId,
      id:
        rawData.data.id ||
        rawData.result.id ||
        rawData.result.eventId ||
        rawData.result.invocationId,
    };
  }
  return rawData;
};

/**
 * 众安信科 openApi 默认 transport。
 *
 * 这层只负责把 SDK 标准 transport 接口映射到当前网关接口；
 * 其它业务项目不要复制 useADKChat，只需要实现自己的 transport 即可。
 */
export const createXGroupOpenApiTransport = ({
  url,
  appNo,
  token,
  pageSize = SESSION_PAGE_SIZE,
  onAuthError,
}: XGroupOpenApiTransportOptions): AdkChatTransport => {
  const getAuthHeaders = () => {
    const tokenValue = getTokenValue(token);
    return tokenValue ? { Authorization: `Bearer ${tokenValue}` } : {};
  };

  return {
    fetchAppConfig: async () => {
      const { data, result } = await fetchAppConfig({ url, appNo });
      if (result?.code !== API_SUCCESS_CODE) {
        throw Object.assign(new Error(result?.message || "获取应用配置失败"), {
          code: result?.code,
          raw: result,
        });
      }
      return data as any;
    },
    fetchSessions: async (params) => {
      const { data = [], pagination, result } = await fetchSessionList({
        url,
        appNo,
        token: getTokenValue(token) || "",
        pageNum: params?.pageNum || 1,
        pageSize: params?.pageSize || pageSize,
      });
      if (result?.code !== API_SUCCESS_CODE) {
        throw Object.assign(new Error(result?.message || "获取会话列表失败"), {
          code: result?.code,
          raw: result,
        });
      }
      return { data: data as any, pagination };
    },
    fetchSessionEvents: async (sessionId) => {
      const { data = [], result } = await fetchSessionDetail({
        url,
        appNo,
        sessionId,
        token: getTokenValue(token) || "",
      });
      if (result?.code !== API_SUCCESS_CODE) {
        throw Object.assign(new Error(result?.message || "获取会话详情失败"), {
          code: result?.code,
          raw: result,
        });
      }
      return data as Event[];
    },
    sendMessageStreaming: (payload, handlers) =>
      new Promise<void>((resolve, reject) => {
        void fetchEventSource(
          `${url}/api/gateway-web/openApi/v1/aizt/app/${appNo}/sendMsgStreaming`,
          {
            method: "POST",
            signal: handlers.signal,
            body: JSON.stringify(payload),
            openWhenHidden: true,
            headers: {
              "Content-Type": "application/json",
              "Cache-Control": "no-cache",
              stream: payload.stream === false ? "false" : "true",
              ...getAuthHeaders(),
            } as Record<string, string>,
            async onopen(response) {
              handlers.onOpen?.(response);
              if (
                response.ok &&
                response.headers.get("content-type") === EventStreamContentType
              ) {
                return;
              }
              if (response.status === 401 || response.status === 403) {
                onAuthError?.(response);
              }
              throw Object.assign(
                new Error(`流式请求失败 (${response.status})`),
                { status: response.status },
              );
            },
            onmessage: (event) => {
              const rawData = safeJsonParse(event.data, {});
              const chunk = normalizeStreamEvent(rawData);
              if (chunk.error) {
                // transport 不直接弹 UI，错误交给 runtime callbacks 或业务层处理。
                handlers.onError?.(chunk.error);
                return;
              }
              handlers.onEvent(chunk);
            },
            onclose: () => {
              handlers.onClose?.();
              resolve();
            },
            onerror: (error) => {
              handlers.onError?.(error);
              reject(error);
              throw error;
            },
          },
        );
      }),
    closeSession: async (sessionId) => {
      const { result } = await closeSession({
        url,
        appNo,
        sessionId,
        token: getTokenValue(token) || "",
      });
      return result?.code === API_SUCCESS_CODE;
    },
    updateSessionTitle: async (sessionId, title) => {
      const { result } = await updateSessionTitle({
        url,
        appNo,
        sessionId,
        title,
        token: getTokenValue(token) || "",
      });
      return result?.code === API_SUCCESS_CODE;
    },
    feedback: async ({ invocationId, isLike, ...feedbackData }) => {
      const { result } = await addFeedback({
        url,
        appNo,
        token: getTokenValue(token) || "",
        requestId: invocationId,
        isLike,
        ...feedbackData,
      });
      return result?.code === API_SUCCESS_CODE;
    },
  };
};
